/**
 * Data migration utilities for enhancing existing shipment costing records
 * with VAT breakdown fields
 */

import { calculateFromInclusive } from './vat-calculations';
import { validateCostingData } from './shipment-costing-validation';
import { DEFAULT_COSTING_DATA } from '../types/shipment-costing-types';

/**
 * Migrates a legacy costing record to the enhanced VAT-aware format
 * @param {Object} legacyCosting - Legacy costing record
 * @returns {Object} Enhanced costing record with VAT breakdown
 */
export const migrateLegacyCostingRecord = (legacyCosting) => {
  const migrated = {
    ...DEFAULT_COSTING_DATA,
    ...legacyCosting
  };
  
  // Migrate fuel costs
  if (legacyCosting.fuel && parseFloat(legacyCosting.fuel) > 0) {
    const fuelAmount = parseFloat(legacyCosting.fuel);
    
    // Assume legacy amounts included VAT (default behavior)
    migrated.fuelAmount = fuelAmount;
    migrated.fuelIncludesVAT = true;
    
    // Calculate VAT breakdown
    const fuelBreakdown = calculateFromInclusive(fuelAmount);
    migrated.fuelBaseAmount = fuelBreakdown.baseAmount;
    migrated.fuelVATAmount = fuelBreakdown.vatAmount;
  }
  
  // Migrate maintenance costs
  if (legacyCosting.maintenance && parseFloat(legacyCosting.maintenance) > 0) {
    const maintenanceAmount = parseFloat(legacyCosting.maintenance);
    
    // Assume legacy amounts included VAT (default behavior)
    migrated.maintenanceAmount = maintenanceAmount;
    migrated.maintenanceIncludesVAT = true;
    
    // Calculate VAT breakdown
    const maintenanceBreakdown = calculateFromInclusive(maintenanceAmount);
    migrated.maintenanceBaseAmount = maintenanceBreakdown.baseAmount;
    migrated.maintenanceVATAmount = maintenanceBreakdown.vatAmount;
  }
  
  // Migrate commission (VAT-exempt, no breakdown needed)
  if (legacyCosting.commission && parseFloat(legacyCosting.commission) > 0) {
    migrated.commissionAmount = parseFloat(legacyCosting.commission);
  }
  
  // Set migration metadata
  migrated.vatCalculationVersion = '1.0';
  migrated.vatCalculatedAt = new Date();
  migrated.vatCalculatedBy = 'migration-script';
  
  // Preserve legacy fields for backward compatibility
  migrated.fuel = legacyCosting.fuel || 0;
  migrated.maintenance = legacyCosting.maintenance || 0;
  migrated.commission = legacyCosting.commission || 0;
  
  return migrated;
};

/**
 * Checks if a costing record needs migration
 * @param {Object} costingRecord - Costing record to check
 * @returns {boolean} True if migration is needed
 */
export const needsMigration = (costingRecord) => {
  // Check if record has new VAT fields
  const hasNewFields = costingRecord.hasOwnProperty('fuelIncludesVAT') ||
                      costingRecord.hasOwnProperty('maintenanceIncludesVAT') ||
                      costingRecord.hasOwnProperty('fuelBaseAmount') ||
                      costingRecord.hasOwnProperty('maintenanceBaseAmount');
  
  // Check if record has legacy fields with values
  const hasLegacyData = (costingRecord.fuel && parseFloat(costingRecord.fuel) > 0) ||
                       (costingRecord.maintenance && parseFloat(costingRecord.maintenance) > 0) ||
                       (costingRecord.commission && parseFloat(costingRecord.commission) > 0);
  
  // Needs migration if it has legacy data but no new fields
  return Boolean(hasLegacyData && !hasNewFields);
};

/**
 * Validates a migrated record
 * @param {Object} migratedRecord - The migrated record to validate
 * @param {Object} originalRecord - The original record for comparison
 * @returns {Object} Validation result with details
 */
export const validateMigratedRecord = (migratedRecord, originalRecord) => {
  const validation = validateCostingData(migratedRecord);
  const issues = [];
  
  // Check that total amounts are preserved
  const originalFuel = parseFloat(originalRecord.fuel) || 0;
  const originalMaintenance = parseFloat(originalRecord.maintenance) || 0;
  const originalCommission = parseFloat(originalRecord.commission) || 0;
  
  const migratedFuelTotal = migratedRecord.fuelBaseAmount + migratedRecord.fuelVATAmount;
  const migratedMaintenanceTotal = migratedRecord.maintenanceBaseAmount + migratedRecord.maintenanceVATAmount;
  const migratedCommissionTotal = migratedRecord.commissionAmount;
  
  const tolerance = 0.01;
  
  if (Math.abs(originalFuel - migratedFuelTotal) > tolerance) {
    issues.push(`Fuel amount mismatch: original ${originalFuel}, migrated total ${migratedFuelTotal}`);
  }
  
  if (Math.abs(originalMaintenance - migratedMaintenanceTotal) > tolerance) {
    issues.push(`Maintenance amount mismatch: original ${originalMaintenance}, migrated total ${migratedMaintenanceTotal}`);
  }
  
  if (Math.abs(originalCommission - migratedCommissionTotal) > tolerance) {
    issues.push(`Commission amount mismatch: original ${originalCommission}, migrated ${migratedCommissionTotal}`);
  }
  
  return {
    isValid: validation.isValid && issues.length === 0,
    errors: [...validation.errors, ...issues],
    warnings: []
  };
};

/**
 * Creates a migration summary for a batch of records
 * @param {Array} migrationResults - Array of migration results
 * @returns {Object} Migration summary
 */
export const createMigrationSummary = (migrationResults) => {
  const summary = {
    totalRecords: migrationResults.length,
    successfulMigrations: 0,
    failedMigrations: 0,
    skippedRecords: 0,
    errors: [],
    warnings: []
  };
  
  migrationResults.forEach((result, index) => {
    if (result.success && result.skipped) {
      summary.skippedRecords++;
    } else if (result.success) {
      summary.successfulMigrations++;
    } else {
      summary.failedMigrations++;
      summary.errors.push({
        recordIndex: index,
        recordId: result.recordId,
        error: result.error
      });
    }
    
    if (result.warnings && result.warnings.length > 0) {
      summary.warnings.push({
        recordIndex: index,
        recordId: result.recordId,
        warnings: result.warnings
      });
    }
  });
  
  return summary;
};

/**
 * Generates a rollback record that can restore the original data
 * @param {Object} originalRecord - Original record before migration
 * @param {Object} migratedRecord - Record after migration
 * @returns {Object} Rollback information
 */
export const generateRollbackData = (originalRecord, migratedRecord) => {
  return {
    recordId: originalRecord.id,
    originalData: {
      fuel: originalRecord.fuel,
      maintenance: originalRecord.maintenance,
      commission: originalRecord.commission
    },
    migratedData: {
      fuelAmount: migratedRecord.fuelAmount,
      fuelIncludesVAT: migratedRecord.fuelIncludesVAT,
      fuelBaseAmount: migratedRecord.fuelBaseAmount,
      fuelVATAmount: migratedRecord.fuelVATAmount,
      maintenanceAmount: migratedRecord.maintenanceAmount,
      maintenanceIncludesVAT: migratedRecord.maintenanceIncludesVAT,
      maintenanceBaseAmount: migratedRecord.maintenanceBaseAmount,
      maintenanceVATAmount: migratedRecord.maintenanceVATAmount,
      commissionAmount: migratedRecord.commissionAmount
    },
    migrationTimestamp: new Date(),
    canRollback: true
  };
};

/**
 * Applies rollback to restore original data
 * @param {Object} rollbackData - Rollback information
 * @param {Object} currentRecord - Current record to rollback
 * @returns {Object} Rolled back record
 */
export const applyRollback = (rollbackData, currentRecord) => {
  const rolledBack = { ...currentRecord };
  
  // Restore original values
  rolledBack.fuel = rollbackData.originalData.fuel;
  rolledBack.maintenance = rollbackData.originalData.maintenance;
  rolledBack.commission = rollbackData.originalData.commission;
  
  // Remove migrated fields
  delete rolledBack.fuelAmount;
  delete rolledBack.fuelIncludesVAT;
  delete rolledBack.fuelBaseAmount;
  delete rolledBack.fuelVATAmount;
  delete rolledBack.maintenanceAmount;
  delete rolledBack.maintenanceIncludesVAT;
  delete rolledBack.maintenanceBaseAmount;
  delete rolledBack.maintenanceVATAmount;
  delete rolledBack.commissionAmount;
  delete rolledBack.vatCalculationVersion;
  delete rolledBack.vatCalculatedAt;
  delete rolledBack.vatCalculatedBy;
  
  return rolledBack;
};